// Single place where the three access tables are read: user_roles,
// organization_members and profiles.
//
// Three rules live here, and nowhere else:
//   1. A query that SUCCEEDS with zero rows means "no access".
//   2. A query that FAILS or is ABORTED means "we do not know" — never "no access".
//   3. Each table is read once per signed-in session; concurrent callers share
//      the same in-flight promise.

import { supabase } from '@/integrations/supabase/client';

export type Outcome<T> =
  | { status: 'ok'; data: T }
  | { status: 'unknown'; error: string; aborted: boolean };

export const isAbortError = (err: any): boolean => {
  if (!err) return false;
  const name = err.name ?? err.code ?? '';
  const msg = String(err.message ?? err.details ?? err ?? '');
  return (
    name === 'AbortError' ||
    name === '20' ||
    /abort/i.test(name) ||
    /aborted|AbortError|signal is aborted/i.test(msg)
  );
};

/** Counts real network reads per table, for diagnostics. */
export const authQueryStats: Record<string, number> = {
  user_roles: 0,
  organization_members: 0,
  profiles: 0,
  event_staff: 0,
};

export const resetAuthQueryStats = () => {
  Object.keys(authQueryStats).forEach((k) => {
    authQueryStats[k] = 0;
  });
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Three attempts with backoff before we admit failure. */
export async function withRetry<T>(
  label: string,
  run: () => Promise<{ data: T | null; error: any }>,
  attempts = 3
): Promise<Outcome<T>> {
  let lastError: any = null;
  let aborted = false;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      authQueryStats[label] = (authQueryStats[label] ?? 0) + 1;
      const res = await run();
      if (!res?.error) {
        return { status: 'ok', data: (res?.data ?? null) as T };
      }
      lastError = res.error;
    } catch (err) {
      lastError = err;
    }

    aborted = isAbortError(lastError);
    if (aborted) {
      // The page moved on. Not a refusal, and not worth shouting about.
      return { status: 'unknown', error: 'aborted', aborted: true };
    }
    if (attempt < attempts) await sleep(attempt === 1 ? 300 : 900);
  }

  console.warn(`authQueries: ${label} could not be read after ${attempts} attempts`, lastError);
  return {
    status: 'unknown',
    error: lastError?.message ?? 'Could not load your permissions.',
    aborted: false,
  };
}

type CacheEntry<T> = { userId: string; value: T };

function makeCachedLoader<T>(
  label: string,
  loader: (userId: string) => Promise<Outcome<T>>
) {
  let cache: CacheEntry<T> | null = null;
  let inflight: { userId: string; promise: Promise<Outcome<T>> } | null = null;

  const load = (userId: string, force = false): Promise<Outcome<T>> => {
    if (!force && cache && cache.userId === userId) {
      return Promise.resolve({ status: 'ok', data: cache.value });
    }
    if (inflight && inflight.userId === userId && !force) return inflight.promise;

    const promise = loader(userId).then((out) => {
      if (out.status === 'ok') cache = { userId, value: out.data };
      if (inflight?.promise === promise) inflight = null;
      return out;
    });
    inflight = { userId, promise };
    return promise;
  };

  load.clear = () => {
    cache = null;
    inflight = null;
  };
  load.label = label;
  return load;
}

export const loadUserRoles = makeCachedLoader<string[]>('user_roles', async (userId) => {
  const out = await withRetry<any[]>('user_roles', () =>
    (supabase as any).from('user_roles').select('role').eq('user_id', userId)
  );
  if (out.status !== 'ok') return out;
  return { status: 'ok', data: (out.data ?? []).map((r: any) => r.role).filter(Boolean) };
});

export const loadOrgMemberships = makeCachedLoader<string[]>(
  'organization_members',
  async (userId) => {
    const out = await withRetry<any[]>('organization_members', () =>
      (supabase as any)
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', userId)
        .eq('status', 'active')
    );
    if (out.status !== 'ok') return out;
    return { status: 'ok', data: (out.data ?? []).map((m: any) => m.organization_id) };
  }
);

export const loadEventStaff = makeCachedLoader<boolean>('event_staff', async (userId) => {
  const out = await withRetry<any[]>('event_staff', () =>
    (supabase as any)
      .from('event_staff')
      .select('id')
      .eq('user_id', userId)
      .eq('is_active', true)
      .limit(1)
  );
  if (out.status !== 'ok') return out;
  return { status: 'ok', data: (out.data ?? []).length > 0 };
});

export const loadProfile = makeCachedLoader<any>('profiles', async (userId) => {
  const out = await withRetry<any>('profiles', () =>
    (supabase as any).from('profiles').select('*').eq('user_id', userId).maybeSingle()
  );
  return out;
});

/** Called on sign-out and on an explicit Retry. */
export function clearAuthCaches() {
  loadUserRoles.clear();
  loadOrgMemberships.clear();
  loadEventStaff.clear();
  loadProfile.clear();
}
